/* eslint-disable no-param-reassign */
import Prism from 'prismjs';
import cookies from 'js-cookie';
import 'prismjs/themes/prism.css';
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-r';
import 'prismjs/components/prism-bash';

/* eslint-disable no-undef */
export default {
  data() {
    return {
      selectedCodeLanguageDropdownOpened: false,
      selectedCodeLanguage: 'javascript',
      imageSizes: [],
    };
  },
  mounted() {
    this.$root.$on('emit-click', this.onEmit);
  },
  beforeDestroy() {
    this.$root.$off('emit-click', this.onEmit);
  },
  computed: {
    codeLanguages() {
      return {
        javascript: 'Javascript',
        python: 'Python',
        ruby: 'Ruby',
        php: 'PHP',
        java: 'Java',
        c: 'C',
        csharp: 'C#',
        cpp: 'C++',
        html: 'HTML',
        sql: 'SQL',
        r: 'R',
        sh: 'Shell',
      };
    },
  },
  watch: {
    selectedCodeLanguageDropdownOpened() {
      const images = tinymce.activeEditor.dom.select('img', tinymce.activeEditor.getBody());

      if (images && images.length > 0) {
        this.imageSizes = images.map((img) => ({
          src: img.src,
          width: window.getComputedStyle(img).width,
          height: window.getComputedStyle(img).height,

        }));
      }

      if (!this.selectedCodeLanguageDropdownOpened) {
        this.closeAllCodeblockDropdowns();
      }
    },
  },
  methods: {
    onEmit(e) {
      // Close all menus only if target is not a codeblock dropdown or codeblock language label
      // this check is needed because the click event is emitted multiple times for same element
      if (!e.target.classList.contains('codeblock-select-icon') && !e.target.closest('.editor-language-dropdown-options')) {
        this.closeAllCodeblockDropdowns();
      }

      // if click is not emitted from this instance, return
      if (!this.$el.contains(e.target)) {
        return;
      }

      if (this.switchMode !== 'blank') {
        this.toggleCloseIcon(e);

        this.toggleLanguageDropdown(e);
      }
    },
    setCursorLocationToEnd(element) {
      const { childNodes } = element;
      if (childNodes && childNodes.length) {
        tinymce.activeEditor.selection.select(childNodes[childNodes.length - 1], 0);
        tinymce.activeEditor.selection.collapse(0);
      } else {
        tinymce.activeEditor.selection.setCursorLocation(element, 0);
      }
    },
    checkIfCodeBlockHasHorizontalScrollbar() {
      const codeBlocks = this.$el.querySelectorAll('pre');
      codeBlocks.forEach((codeBlock) => {
        const code = codeBlock.querySelector('code');
        if (code) {
          if (code.scrollWidth > code.clientWidth) {
            codeBlock.classList.add('scrollable');
          } else {
            codeBlock.classList.remove('scrollable');
          }
        }
        if (codeBlock.querySelector('.codeblock-close-icon')) {
          codeBlock.querySelector('.codeblock-close-icon').style.transform = 'translateX(0px)';
        }
        if (codeBlock.querySelector('.codeblock-select-icon')) {
          codeBlock.querySelector('.codeblock-select-icon').style.transform = 'translateX(0px)';
        }
        codeBlock.addEventListener('scroll', (e) => {
          if (codeBlock.querySelector('.codeblock-close-icon')) {
            codeBlock.querySelector('.codeblock-close-icon').style.transform = `translateX(${e.target.scrollLeft}px)`;
          }
          if (codeBlock.querySelector('.codeblock-select-icon')) {
            codeBlock.querySelector('.codeblock-select-icon').style.transform = `translateX(${e.target.scrollLeft}px)`;
          }
        });
        // TODO: This causes bugs in codeblock navigation, so it is commented out for now
        // Prism.highlightElement(codeBlock.querySelector('code'));
      });
    },
    selectCodeLanguage(language, e) {
      const prevLanguage = this.selectedCodeLanguage;

      this.selectedCodeLanguage = language;

      if (!this.selectedModeOption || this.selectedModeOption === 'text') {
        const pre = tinymce.activeEditor.bodyElement.querySelector('.selected-pre');

        pre.classList.forEach((c) => {
          if (c.includes('language-')) {
            pre.classList.remove(c);
          }
        });
        pre.classList.add(`language-${language}`);
        pre.setAttribute('data-language-key', language);
        pre.setAttribute('data-language-label', this.codeLanguages[language]);

        const label = pre.querySelector('.codeblock-select-icon');
        if (label) {
          label.setAttribute('data-language-label', this.codeLanguages[language]);
        }

        const codeElement = pre.querySelector('code');
        codeElement.classList.remove(`language-${prevLanguage}`);
        codeElement.classList.add(`language-${language}`);
        codeElement.classList.add('focus');

        const code = codeElement.innerText;
        if (code) {
          codeElement.innerHTML = Prism.highlight(code, Prism.languages[language], language);
        }

        pre.classList.remove('selected-pre');
        setTimeout(() => {
          this.closeAllCodeblockDropdowns();
        }, 10);

        const buffer = document.createElement('div');
        buffer.innerHTML = tinymce.activeEditor.getContent();

        tinymce.activeEditor.setContent(buffer.innerHTML);
        setTimeout(() => {
          const focusedCode = document.querySelector('pre code.focus');
          if (focusedCode) {
            // Focus inside the codeblock
            this.setCursorLocationToEnd(focusedCode);
          }
        }, 100);

        if (e) {
          e.target.closest('.editor-language-dropdown-options').classList.remove('opened');
        }
      } else {
        const code = Prism.highlight(tinymce.activeEditor.bodyElement.innerText, Prism.languages[language], language);

        const closeIcon = this.selectedMode === 'code' && this.mode === 'answer' ? '' : '<svg class="codeblock-close-icon" width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.3792 4.44688C12.6225 4.20353 12.6226 3.80898 12.3792 3.56562C12.1358 3.32227 11.7413 3.32227 11.4979 3.56563L8.44482 6.61875L5.3917 3.56563C5.14835 3.32228 4.7538 3.32227 4.51045 3.56562C4.2671 3.80898 4.2671 4.20352 4.51045 4.44687L7.56357 7.5L4.51045 10.5531C4.2671 10.7965 4.2671 11.191 4.51045 11.4344C4.7538 11.6777 5.14835 11.6777 5.3917 11.4344L8.44482 8.38125L11.4979 11.4344C11.7413 11.6777 12.1358 11.6777 12.3792 11.4344C12.6226 11.191 12.6226 10.7965 12.3792 10.5531L9.32607 7.5L12.3792 4.44688Z" fill="#545C69"/></svg>';
        const languageIcon = `<label class="codeblock-select-icon mceNonEditable" data-language-label="${this.codeLanguages[language]}"></label>`;
        tinymce.activeEditor.setContent(`<pre data-language-label="${this.codeLanguages[language]}" data-keep-tags="span,br" data-language-key="${language}" class="codeblock mceNonEditable language-${language}">${languageIcon}${closeIcon}<code class="language-${language} mceEditable">${code}</code></pre>&nbsp;`);

        // Focus inside the codeblock
        const codeNode = tinymce.activeEditor.getBody().querySelector('pre code');
        if (codeNode) {
          this.setCursorLocationToEnd(codeNode);
        }
      }

      this.selectedCodeLanguageDropdownOpened = false;
      const dropdowns = e.target.closest('.tinymce-editor').querySelectorAll('.editor-language-dropdown-options');
      dropdowns.forEach((d) => d.classList.remove('opened'));

      this.closeAllCodeblockDropdowns();
      tinymce.activeEditor.getElement().dispatchEvent(new Event('input'));

      if (window.location.href.includes('courses/edit')) {
        cookies.set('codeblock_language', language);
      }

      const newImages = tinymce.activeEditor.dom.select('img', tinymce.activeEditor.getBody());

      if (newImages && newImages.length > 0) {
        newImages.forEach((newImg) => {
          const originalSize = this.imageSizes.find((img) => img.src === newImg.src);
          if (originalSize) {
            newImg.style.setProperty('width', originalSize.width, 'important');
            newImg.style.setProperty('height', originalSize.height, 'important');
          }
        });
      }
    },
    toggleCloseIcon(e) {
      if (e.target.classList.contains('codeblock-close-icon') || e.target.closest('.codeblock-close-icon')) {
        if (e.target.closest('.tinymce-editor') && e.target.closest('.tinymce-editor').classList.contains('expanded')) {
          const cblock = e.target.closest('.codeblock');
          if (cblock) {
            cblock.remove();
          }
          tinymce.activeEditor.execCommand('mceInsertContent', true, '');
        }
      }
    },
    toggleLanguageDropdown(e) {
      const editor = e.target.closest('.tinymce-editor');
      if (e.target.tagName === 'LABEL' && e.target.classList.contains('codeblock-select-icon')) {
        const pre = e.target.closest('pre');
        if (editor && editor.classList.contains('expanded')) {
          // Menu position
          const editorPosition = editor.getBoundingClientRect();
          const dropdown = editor.querySelector('.editor-language-dropdown-options');
          const dropdownPosition = dropdown.getBoundingClientRect();
          const labelPosition = e.target.getBoundingClientRect();
          let top = labelPosition.top - editorPosition.top + labelPosition.height + 4;
          const left = labelPosition.left - editorPosition.left;

          if (window.innerHeight < labelPosition.top + dropdownPosition.height + labelPosition.height + 4) {
            top = labelPosition.top - editorPosition.top - dropdownPosition.height - 4;
          }

          if (this.selectedCodeLanguageDropdownOpened) {
            dropdown.classList.remove('opened');
            this.selectedCodeLanguageDropdownOpened = false;
            editor.classList.remove('opened-codeblock-dropdown');
          } else {
            if (dropdown) {
              dropdown.classList.add('opened');
            }

            editor.classList.add('opened-codeblock-dropdown');
            dropdown.style.top = `${top}px`;
            dropdown.style.left = `${left}px`;
            this.selectedCodeLanguageDropdownOpened = true;
          }

          // By this class we can identify the selected/clicked codeblock
          pre.classList.add('selected-pre');
        }
      } else if (!e.target.closest('.editor-language-dropdown-options')) {
        const dropdowns = this.$el.querySelectorAll('.editor-language-dropdown-options');
        dropdowns.forEach((d) => d.classList.remove('opened'));

        // Remove all opened-codeblock-dropdown classes from tinymce editors
        this.closeAllCodeblockDropdowns();

        this.selectedCodeLanguageDropdownOpened = false;
      }
    },
    closeAllCodeblockDropdowns() {
      Array.from(document.querySelectorAll('.tinymce-editor.opened-codeblock-dropdown')).forEach((editor) => editor.classList.remove('opened-codeblock-dropdown'));
      Array.from(document.querySelectorAll('pre.selected-pre')).forEach((pre) => pre.classList.remove('selected-pre'));
      Array.from(document.querySelectorAll('.editor-language-dropdown-options')).forEach((d) => d.classList.remove('opened'));
      this.selectedCodeLanguageDropdownOpened = false;
    },
  },
};
